import React, { memo } from "react";
import SlideOutSidebar from "../../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { Form, Formik, FormikProps } from "formik";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import XGSFormTextarea from "../../../../ui-components/form/textarea/xgsFormTextarea";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ImportantMessage, { ImportantMessageTheme } from "../../../../ui-components/molecules/important-message/importantMessage";
import { AccessorialApprovalStatus, BillingAccessorialApprovalRequest, BillingAccessorialApprovalRequestValidationSchema, ShipmentWorkItemAccessorial, ShipmentWorkItemReviewStatus } from "../../../../app/data/shipment-reconciliation/models";
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import { BillingAccessorialApprovalState, BillingReviewListState, BillingReviewSummaryState, ShipmentWorkItemAccessorialsState } from "../../../../slices/shipment-reconciliation/ShipmentReconciliationState";
import { billingAccessorialApprovalSelector, submitBillingAccessorialApproval } from "../../../../slices/shipment-reconciliation/billing-review/billingAccessorialsApprovalSlice";
import { changeBillingReviewListLocally, getBillingReviewListSelector } from "../../../../slices/shipment-reconciliation/billing-review/getBillingReviewListSlice";
import { changeBillingReviewSummaryLocally, getBillingReviewSummarySelector } from "../../../../slices/shipment-reconciliation/billing-review/getBillingReviewSummarySlice";
import { shipmentWorkItemAccessorialsSelector } from "../../../../slices/shipment-reconciliation/shipmentWorkItemAccessorialsSlice";
import "./billingAccessorialApproval.scss";


interface OperationAccessorialApprovalProps {
  shipmentWorkItemId: number,
  accessorialId: string,
  approvalStatus: AccessorialApprovalStatus,
  accessorialName: string,
  accessorialRate: string,
  onClose: any,
  onSuccess: any
}

const OperationAccessorialApproval: React.FC<OperationAccessorialApprovalProps> = memo(({ shipmentWorkItemId, accessorialId, approvalStatus, accessorialName, accessorialRate, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const formRef = React.useRef<FormikProps<BillingAccessorialApprovalRequest>>(null);

  const billingAccessorialApprovalState: BillingAccessorialApprovalState = useSelector(
    billingAccessorialApprovalSelector
  );
  const billingReviewList: BillingReviewListState = useSelector(
    getBillingReviewListSelector
  );
  const billingReviewSummary: BillingReviewSummaryState = useSelector(
    getBillingReviewSummarySelector
  );
  const shipmentWorkItemAccessorials: ShipmentWorkItemAccessorialsState = useSelector(
    shipmentWorkItemAccessorialsSelector
  );

  const adjustBillingReviewSummaryAndList = () => {
    let currentReviewSummary = { ...billingReviewSummary.summary };
    let currentReviewList = [...billingReviewList.shipmentWorkItems];
    let currentAccessorialsList = [...shipmentWorkItemAccessorials.accessorials];

    let affectedWorkItemIndex = currentReviewList.findIndex((ele) => ele.id === shipmentWorkItemId);
    if (affectedWorkItemIndex === -1) return;

    let affectedWorkItem = { ...currentReviewList[affectedWorkItemIndex] };

    // check if the there is any pending accessorial
    let isReviewComplete = true;
    if (currentAccessorialsList && currentAccessorialsList.length) {
      currentAccessorialsList.forEach(accessorial => {
        if (accessorial && accessorial.id !== accessorialId && accessorial.billingApprovalStatus === AccessorialApprovalStatus.PENDING) {
          isReviewComplete = false;
        }
      });
    }

    if (isReviewComplete && affectedWorkItem.billingReviewStatus !== ShipmentWorkItemReviewStatus.REVIEWED) {
      currentReviewSummary.compltedBillingReview += 1;
      currentReviewSummary.pendingBillingReview -= 1;
      affectedWorkItem.billingReviewStatus = ShipmentWorkItemReviewStatus.REVIEWED;
    }
    let affectedAccessorials: ShipmentWorkItemAccessorial[] = [];
    if (affectedWorkItem.accessorials && affectedWorkItem.accessorials.length) {
      affectedWorkItem.accessorials.forEach((accessorial: ShipmentWorkItemAccessorial) => {
        if (accessorial) {
          let affectedAccessorial = { ...accessorial };
          if (accessorial.id === accessorialId) {
            affectedAccessorial.billingApprovalStatus = approvalStatus;
          }
          affectedAccessorials.push(affectedAccessorial);
        }

      })
    }
    affectedWorkItem.accessorials = affectedAccessorials;
    currentReviewList[affectedWorkItemIndex] = affectedWorkItem;
    dispatch(changeBillingReviewListLocally({
      shipmentWorkItems: currentReviewList
    }));
    dispatch(changeBillingReviewSummaryLocally({
      summary: currentReviewSummary
    }));
  }

  const onSubmit = (data: BillingAccessorialApprovalRequest) => {
    if (data.approvalStatus === AccessorialApprovalStatus.RJECTED && !data.notes) {
      toast.error("Please provide rejection reason while rejecting an accessorial");
      return;
    }
    if (data.approvalStatus === AccessorialApprovalStatus.ACCEPTED && !data.billingRate) {
      toast.error("Please provide the amount for accesorial charge added on the invoice");
      return;
    }
    dispatch(submitBillingAccessorialApproval(
      shipmentWorkItemId,
      accessorialId,
      data,
      () => {
        adjustBillingReviewSummaryAndList();
        toast.info("Accessorial approval status stored successfully.");
        onClose();
        onSuccess && onSuccess();
      },
      (error) => {
        toast.error(error);
      }
    ))
  }

  const getApprovalStatusToDisplay = (approvalStatus: AccessorialApprovalStatus)=>{
    if(approvalStatus === AccessorialApprovalStatus.ACCEPTED) return "Accept";
    else if(approvalStatus === AccessorialApprovalStatus.RJECTED) return "Refuse";
    else return approvalStatus;
  }

  return (
    <SlideOutSidebar
      header="Accessorial Approval"
      onClose={onClose}
      size="extrasmall"
      show={!!(shipmentWorkItemId)}
    >
      <div className="xgs-billing-accessorial-approval">
        <ImportantMessage
          theme={ImportantMessageTheme.BLUE}
        >You are going to {approvalStatus === AccessorialApprovalStatus.ACCEPTED ? 'Accept' : 'Refuse'} the accessorial {accessorialName}.
          {approvalStatus === AccessorialApprovalStatus.ACCEPTED ? 'This means the accessorial is valid for the probill and the charges are added on the Invoice' :
            `This means the accessorial is not valid for the probill and no charges are added to the invoice for this accessorial.`
          }
        </ImportantMessage>
        <Formik
          initialValues={{
            approvalStatus,
            notes: ""
          }}
          onSubmit={onSubmit}
          validationSchema={BillingAccessorialApprovalRequestValidationSchema}
          innerRef={formRef}
        >
          {(props: FormikProps<BillingAccessorialApprovalRequest>) => (
            <Form className="billing-approval-form">
              <XGSFormInput
                type="text"
                name="approvalStatus"
                label="Approval Status"
                value={getApprovalStatusToDisplay(approvalStatus)}
                labelMode={LabelModes.column}
                disabled={true}
              />
              <XGSFormTextarea
                name="notes"
                label="Notes"
                required={false}
                rows={3}
                counter={500}
              />
              {(approvalStatus === AccessorialApprovalStatus.ACCEPTED) && <XGSFormInput
                type="text"
                name="billingRate"
                label="Amount added on Invoice for the Accessorial"
                placeholder="Accessorial Charge"
                labelMode={LabelModes.column}
              />}
              <Button
                type="submit"
                theme={ButtonThemes.blue}
                spinner={billingAccessorialApprovalState.loading}
                disabled={billingAccessorialApprovalState.loading}
                className="billing-approval-form__submit"
              >
                Submit
              </Button>
            </Form>
          )}
        </Formik>
        <div className="submit-accessorial-approval-actions">
        </div>
      </div>
    </SlideOutSidebar>
  );
});

export default OperationAccessorialApproval;
